import { useEffect, useMemo, useState } from "react";
import axios from "@/api/api";
import { API_ROUTES } from "@/api/routes";
import { toast } from "react-toastify";
import { useSession } from "@/context/SessionProvider";

const MANAGE_ROLES = new Set([1]);

const money = (v) =>
    Number(v || 0).toLocaleString("es-MX", {
        style: "currency",
        currency: "MXN",
        minimumFractionDigits: 2,
    });

export default function Listado() {
    const { user, loading: sessionLoading } = useSession();
    const perfilId = user?.perfil_id ?? null;
    const canManage = MANAGE_ROLES.has(Number(perfilId));

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [items, setItems] = useState([]);
    const [q, setQ] = useState("");
    const [newItems, setNewItems] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState(null);

    useEffect(() => {
        if (!sessionLoading) fetchProductos();
    }, [sessionLoading]);

    const fetchProductos = async () => {
        try {
            setLoading(true);
            const res = await axios.get(API_ROUTES.productosList, { withCredentials: true });
            setItems(Array.isArray(res.data) ? res.data : (res.data?.items ?? []));
        } catch (e) {
            console.error(e);
            toast.error("Error al cargar productos.");
        } finally {
            setLoading(false);
        }
    };

    const addNewRow = () => {
        setNewItems([...newItems, { id: Date.now(), nombre: "", descripcion: "", precio: "" }]);
    };

    const updateNewItem = (id, field, value) => {
        setNewItems(newItems.map(item => item.id === id ? { ...item, [field]: value } : item));
    };

    const removeNewItem = (id) => {
        setNewItems(newItems.filter(item => item.id !== id));
    };

    const saveAllNew = async () => {
        const valid = newItems.filter(item => item.nombre.trim().length >= 3 && Number(item.precio) >= 0.01);
        if (valid.length === 0) return toast.error("Completa al menos un producto válido.");

        try {
            setSaving(true);
            const promises = valid.map(item =>
                axios.post(API_ROUTES.productosCreate, {
                    nombre: item.nombre.trim(),
                    descripcion: item.descripcion?.trim() || null,
                    precio: Number(item.precio),
                    moneda: "MXN",
                }, { withCredentials: true })
            );
            await Promise.all(promises);
            toast.success(`${valid.length} producto(s) creado(s).`);
            setNewItems([]);
            fetchProductos();
        } catch (e) {
            console.error(e);
            toast.error("Error al guardar productos.");
        } finally {
            setSaving(false);
        }
    };

    const updateItem = (id, field, value) => {
        setItems(items.map(item => item.id === id ? { ...item, [field]: value } : item));
    };

    const saveEdit = async (id) => {
        const item = items.find(i => i.id === id);
        if (!item || item.nombre.trim().length < 3 || Number(item.precio) < 0.01) return;

        try {
            await axios.put(`${API_ROUTES.productosUpdate}/${id}`, {
                nombre: item.nombre.trim(),
                descripcion: item.descripcion?.trim() || null,
                precio: Number(item.precio),
                moneda: "MXN",
            }, { withCredentials: true });
            toast.success("Producto actualizado.");
            setEditingId(null);
            fetchProductos();
        } catch (e) {
            console.error(e);
            toast.error("Error al actualizar.");
        }
    };

    const deleteItem = async (id) => {
        try {
            await axios.delete(`${API_ROUTES.productosDelete}/${id}`, { withCredentials: true });
            toast.success("Producto eliminado.");
            setDeleteConfirm(null);
            fetchProductos();
        } catch (e) {
            console.error(e);
            toast.error("Error al eliminar.");
        }
    };

    const filtered = useMemo(() => {
        const t = q.trim().toLowerCase();
        return t ? items.filter(p => p.nombre?.toLowerCase().includes(t) || p.descripcion?.toLowerCase().includes(t)) : items;
    }, [items, q]);

    return (
        <div className="p-6 space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Productos</h1>
                    <p className="text-sm text-gray-600">Gestiona tu catálogo de productos</p>
                </div>
                <div className="flex gap-3">
                    <input
                        placeholder="Buscar..."
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        className="w-64 border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    />
                    {canManage && (
                        <button onClick={addNewRow} className="bg-cyan-600 hover:bg-cyan-700 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            Agregar
                        </button>
                    )}
                </div>
            </div>

            {/* Tabla */}
            <div className="bg-white rounded-xl shadow-sm border">
                {loading ? (
                    <div className="p-8 space-y-4 animate-pulse">
                        {[...Array(5)].map((_, i) => (
                            <div key={i} className="flex gap-4">
                                <div className="h-4 bg-gray-200 rounded flex-1" />
                                <div className="h-4 bg-gray-200 rounded w-24" />
                                <div className="h-4 bg-gray-200 rounded w-20" />
                            </div>
                        ))}
                    </div>
                ) : (
                    <table className="w-full">
                        <thead className="bg-gray-50 border-b">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Producto</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Descripción</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Precio</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                                {canManage && <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {/* Nuevos items */}
                            {newItems.map((item) => (
                                <tr key={item.id} className="bg-cyan-50 border-b-2 border-cyan-300">
                                    <td className="px-6 py-3">
                                        <input
                                            value={item.nombre}
                                            onChange={(e) => updateNewItem(item.id, "nombre", e.target.value)}
                                            placeholder="Nombre del producto"
                                            className="w-full border rounded px-3 py-1.5 text-sm"
                                        />
                                    </td>
                                    <td className="px-6 py-3">
                                        <input
                                            value={item.descripcion}
                                            onChange={(e) => updateNewItem(item.id, "descripcion", e.target.value)}
                                            placeholder="Descripción"
                                            className="w-full border rounded px-3 py-1.5 text-sm"
                                        />
                                    </td>
                                    <td className="px-6 py-3">
                                        <input
                                            type="number"
                                            min="0.01"
                                            step="0.01"
                                            value={item.precio}
                                            onChange={(e) => updateNewItem(item.id, "precio", e.target.value)}
                                            placeholder="0.00"
                                            className="w-full border rounded px-3 py-1.5 text-sm"
                                        />
                                        <div className="text-xs text-gray-500 mt-1">{money(item.precio)}</div>
                                    </td>
                                    <td className="px-6 py-3">
                                        <span className="text-xs px-2.5 py-0.5 rounded-full bg-green-100 text-green-800">Activo</span>
                                    </td>
                                    <td className="px-6 py-3 text-right">
                                        <button onClick={() => removeNewItem(item.id)} className="text-red-600 hover:text-red-800">
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </td>
                                </tr>
                            ))}

                            {/* Items existentes */}
                            {filtered.map((item) => {
                                const isEditing = editingId === item.id;
                                return (
                                    <tr key={item.id} className={`border-b hover:bg-gray-50 ${isEditing ? "bg-amber-50" : ""}`}>
                                        <td className="px-6 py-4">
                                            {isEditing ? (
                                                <input
                                                    value={item.nombre}
                                                    onChange={(e) => updateItem(item.id, "nombre", e.target.value)}
                                                    className="w-full border rounded px-3 py-1.5 text-sm"
                                                />
                                            ) : (
                                                <div className="text-sm font-medium">{item.nombre}</div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            {isEditing ? (
                                                <input
                                                    value={item.descripcion || ""}
                                                    onChange={(e) => updateItem(item.id, "descripcion", e.target.value)}
                                                    className="w-full border rounded px-3 py-1.5 text-sm"
                                                />
                                            ) : (
                                                <div className="text-sm text-gray-600">{item.descripcion || "—"}</div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            {isEditing ? (
                                                <>
                                                    <input
                                                        type="number"
                                                        min="0.01"
                                                        step="0.01"
                                                        value={item.precio}
                                                        onChange={(e) => updateItem(item.id, "precio", e.target.value)}
                                                        className="w-full border rounded px-3 py-1.5 text-sm"
                                                    />
                                                    <div className="text-xs text-gray-500 mt-1">{money(item.precio)}</div>
                                                </>
                                            ) : (
                                                <div className="text-sm font-semibold text-cyan-700">{money(item.precio)}</div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`text-xs px-2.5 py-0.5 rounded-full ${item.activo ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"}`}>
                                                {item.activo ? "Activo" : "Inactivo"}
                                            </span>
                                        </td>
                                        {canManage && (
                                            <td className="px-6 py-4 text-right">
                                                {isEditing ? (
                                                    <div className="flex justify-end gap-2">
                                                        <button onClick={() => saveEdit(item.id)} className="text-green-600 hover:text-green-800">
                                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                            </svg>
                                                        </button>
                                                        <button onClick={() => setEditingId(null)} className="text-gray-600 hover:text-gray-800">
                                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div className="flex justify-end gap-2">
                                                        <button onClick={() => setEditingId(item.id)} className="text-cyan-600 hover:text-cyan-800">
                                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                            </svg>
                                                        </button>
                                                        <button onClick={() => setDeleteConfirm(item)} className="text-red-600 hover:text-red-800">
                                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                )}
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}

                            {!loading && filtered.length === 0 && newItems.length === 0 && (
                                <tr>
                                    <td colSpan="5" className="px-6 py-16 text-center text-gray-500">
                                        {q ? "Sin resultados" : "No hay productos"}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Botón guardar todos */}
            {newItems.length > 0 && (
                <div className="flex justify-end">
                    <button
                        onClick={saveAllNew}
                        disabled={saving}
                        className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-6 py-3 rounded-lg font-medium"
                    >
                        {saving ? "Guardando..." : `Guardar ${newItems.length} producto(s)`}
                    </button>
                </div>
            )}

            {/* Modal eliminar */}
            {deleteConfirm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-xl p-6 max-w-md w-full">
                        <h3 className="text-lg font-bold mb-2">Eliminar producto</h3>
                        <p className="text-sm text-gray-600 mb-4">¿Eliminar "{deleteConfirm.nombre}"?</p>
                        <div className="flex gap-3">
                            <button onClick={() => setDeleteConfirm(null)} className="flex-1 border rounded-lg py-2">Cancelar</button>
                            <button onClick={() => deleteItem(deleteConfirm.id)} className="flex-1 bg-red-600 text-white rounded-lg py-2">Eliminar</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}